import { OrganizationBusinessStructure, OrganizationDetailFragment } from '@alice-financial/alice-sdk'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextFieldController,
  Typography,
} from '@alice-financial/pretext-ui'
import { MaskedInput } from '@alice-financial/pretext-ui/inputs/MaskedInput'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import * as React from 'react'
import { Control, Controller } from 'react-hook-form'
import { usStates } from '../../../../data/usStates'
import { AddressDisplay } from '../../../employeeDashboard/profile/address/AddressDisplay'
import { PlaceSearchBar } from '../../workLocations/PlaceSearchBar'
import { FinancialInformationForm } from './FinancialInformationForm'
import { OrganizationDetailsFormValues, useOrganizationDetailsForm } from './useOrganizationDetailsForm'
import { hasAllOrgData, hasFinancialInformation, KYCStatus, orgAttributeState } from './kycUtils'

const StatusLabel = ({ status }: { status: KYCStatus }) => {
  if (status === 'complete') return <Chip label="Verified" color="primary" size="small" />
  if (status === 'pending_verification') {
    return <Chip label="Pending verification" color="secondary" size="small" />
  }
  return null
}

const ViewOnlyData = ({ org }: { org: OrganizationDetailFragment }) => {
  const businessStructureLabels: Record<OrganizationBusinessStructure, string> = {
    [OrganizationBusinessStructure.SingleMemberLlc]: 'Single-member LLC',
    [OrganizationBusinessStructure.MultiMemberLlc]: 'Multi-member LLC',
    [OrganizationBusinessStructure.PrivateCorporation]: 'Private Corporation',
  }

  return (
    <Grid container spacing={3} flexDirection="column">
      <Grid size={{ xs: 12, sm: 8 }}>
        <Typography variant="body2" color="text.secondary">
          EIN <StatusLabel status={orgAttributeState(org, 'ein')} />
        </Typography>
        <Typography variant="body1">{org.ein}</Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 8 }}>
        <Typography variant="body2" color="text.secondary">
          Business phone number <StatusLabel status={orgAttributeState(org, 'tel')} />
        </Typography>
        <Typography variant="body1">{org.tel}</Typography>
      </Grid>

      {org.websiteUrl && (
        <Grid size={{ xs: 12, sm: 8 }}>
          <Typography variant="body2" color="text.secondary">
            Website URL <StatusLabel status={orgAttributeState(org, 'websiteUrl')} />
          </Typography>
          <Typography variant="body1">{org.websiteUrl}</Typography>
        </Grid>
      )}

      <Grid size={{ xs: 12, sm: 8 }}>
        <Typography variant="body2" color="text.secondary">
          Business structure <StatusLabel status={orgAttributeState(org, 'businessStructure')} />
        </Typography>
        <Typography variant="body1">
          {org.businessStructure ? businessStructureLabels[org.businessStructure] : ''}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 8 }}>
        <Typography variant="body2" color="text.secondary">
          Business Type <StatusLabel status={orgAttributeState(org, 'mcc')} />
        </Typography>
        <Typography variant="body1">
          {org.mcc?.mcc}: {org.mcc?.label}
        </Typography>
      </Grid>

      <Grid size={12}>
        <Divider />
      </Grid>

      <Grid size={12}>
        <Typography variant="h4" gutterBottom>
          Business Address <StatusLabel status={orgAttributeState(org, 'address')} />
        </Typography>
        <AddressDisplay address={org.address} />
      </Grid>
    </Grid>
  )
}

const ManualAddressInput = ({ control }: { control: Control<OrganizationDetailsFormValues> }) => {
  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <TextFieldController
          name="address.line1"
          control={control}
          label="Address Line 1"
          variant="outlined"
          fullWidth
          rules={{ required: 'Address line 1 is required' }}
        />
      </Grid>
      <Grid size={12}>
        <TextFieldController
          name="address.line2"
          control={control}
          label="Address Line 2"
          variant="outlined"
          fullWidth
        />
      </Grid>
      <Grid size={4}>
        <TextFieldController
          name="address.cityName"
          control={control}
          label="City"
          variant="outlined"
          fullWidth
          rules={{ required: 'City is required' }}
        />
      </Grid>

      <Grid size={4}>
        <TextFieldController
          name="address.stateCode"
          control={control}
          label="State"
          variant="outlined"
          fullWidth
          select
          slotProps={{
            select: {
              native: true,
            },
          }}
          rules={{ required: 'State is required' }}
        >
          <option value=""></option>
          {usStates.map(({ code }) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </TextFieldController>
      </Grid>
      <Grid size={4}>
        <TextFieldController
          name="address.zipcode"
          control={control}
          label="Zip Code"
          variant="outlined"
          fullWidth
          rules={{ required: 'Zip code is required' }}
        />
      </Grid>
    </Grid>
  )
}

const OrganizationDetailsSuccess = ({ org }: { org: OrganizationDetailFragment }) => {
  const addressParts = [org.address?.line1, org.address?.cityName, org.address?.stateCode].filter(Boolean)
  const addressString = addressParts.length > 0 ? addressParts.join(', ') : ''

  return (
    <ListItem>
      <ListItemIcon sx={{ alignSelf: 'center' }}>
        <CheckCircleIcon fontSize="large" color="primary" />
      </ListItemIcon>
      <ListItemText
        primary={<Typography>Business information complete</Typography>}
        secondary={[`EIN: ${org.ein}`, addressString].filter(Boolean).join(' • ')}
      />
    </ListItem>
  )
}

interface OrganizationDetailsFormProps {
  org: OrganizationDetailFragment
}

export const OrganizationDetailsForm = ({ org }: OrganizationDetailsFormProps) => {
  const { control, handleAddressSelect, onSubmit, isLoading, formState, resetForm } =
    useOrganizationDetailsForm(org)
  const [isEditing, setIsEditing] = React.useState(false)

  const financialInfoComplete = hasFinancialInformation(org)
  const orgDataComplete = hasAllOrgData(org)

  // If data exists, start in view mode; otherwise start in edit mode
  React.useEffect(() => {
    setIsEditing(!orgDataComplete)
  }, [orgDataComplete])

  const businessStructureOptions: Array<{ value: OrganizationBusinessStructure; label: string }> = [
    { value: OrganizationBusinessStructure.SingleMemberLlc, label: 'Single-member LLC' },
    { value: OrganizationBusinessStructure.MultiMemberLlc, label: 'Multi-member LLC' },
    { value: OrganizationBusinessStructure.PrivateCorporation, label: 'Private Corporation' },
  ]

  const mccOptions: Array<{ value: string; label: string }> = [
    { value: '5812', label: 'Eating Places, Restaurants' },
    { value: '8062', label: 'Hospitals' },
    { value: '5411', label: 'Grocery Stores, Supermarkets' },
    { value: '8211', label: 'Elementary, Secondary Schools' },
    { value: '8220', label: 'Colleges, Universities' },
    { value: '8099', label: 'Medical Services' },
    { value: '', label: 'Other' },
  ]

  // Format address for PlaceSearchBar initial value
  const formatAddressForSearch = (
    address:
      | {
          line1?: string | null
          line2?: string | null
          cityName?: string | null
          stateCode?: string | null
          zipcode?: string | null
        }
      | null
      | undefined
  ) => {
    if (!address?.line1) return ''
    const parts = [address.line1]
    if (address.line2) parts.push(address.line2)
    parts.push(`${address.cityName}, ${address.stateCode} ${address.zipcode}`)
    return parts.join(', ')
  }

  const initialAddressValue = formatAddressForSearch(org.address)

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleCancel = () => {
    resetForm()
    setIsEditing(false)
  }

  if (orgDataComplete && financialInfoComplete) return <OrganizationDetailsSuccess org={org} />

  if (!isEditing && orgDataComplete) {
    return (
      <>
        <Grid container justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h3">Business Information</Typography>
          <Button variant="outlined" onClick={handleEdit}>
            Edit
          </Button>
        </Grid>
        <ViewOnlyData org={org} />
        {!financialInfoComplete && (
          <Box>
            <Card sx={(theme) => ({ backgroundColor: theme.palette.grey[100], my: 4 })}>
              <CardHeader title="Accounting information" />
              <CardContent>
                <FinancialInformationForm org={org} />
              </CardContent>
            </Card>
          </Box>
        )}
      </>
    )
  }

  return (
    <>
      <form onSubmit={onSubmit}>
        <Typography variant="h3" gutterBottom>
          Business Information
        </Typography>
        <Typography variant="body2" gutterBottom>
          This information will be used to verify your business.
        </Typography>

        <Grid container spacing={3} flexDirection="column">
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextFieldController
              name="businessStructure"
              control={control}
              label="Business Structure"
              variant="outlined"
              fullWidth
              select
              rules={{ required: 'Business structure is required' }}
              slotProps={{
                select: {
                  native: true,
                },
              }}
            >
              <option value="">Select a business structure</option>
              {businessStructureOptions.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </TextFieldController>
          </Grid>

          <Grid size={{ xs: 12, sm: 8 }}>
            <Controller
              name="ein"
              control={control}
              rules={{
                required: 'EIN is required',
                pattern: {
                  value: /^\d{9}$/,
                  message: 'EIN must be 9 digits',
                },
              }}
              render={({ field, formState: { submitCount }, fieldState: { error, isTouched } }) => {
                const isError = Boolean((isTouched || submitCount > 0) && error?.message)
                return (
                  <MaskedInput
                    {...field}
                    format="##-#######"
                    valueIsNumericString
                    label="Employer Identification Number (EIN)"
                    variant="outlined"
                    fullWidth
                    placeholder="12-3456789"
                    error={isError}
                    helperText={isError ? error?.message : undefined}
                  />
                )
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 8 }}>
            <TextFieldController
              name="tel"
              control={control}
              label="Business Phone Number"
              variant="outlined"
              fullWidth
              rules={{ required: 'Business phone number is required' }}
              slotProps={{
                htmlInput: {
                  type: 'tel',
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 8 }}>
            <TextFieldController
              name="websiteUrl"
              control={control}
              label="Website URL"
              variant="outlined"
              fullWidth
              helperText="Share the website where you sell or promote products or services. The website should provide information about the products and services you're selling. Generic URLs or websites under construction aren't supported."
              slotProps={{
                htmlInput: {
                  type: 'url',
                  placeholder: 'https://example.com',
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 8 }}>
            <TextFieldController
              name="mcc"
              control={control}
              label="Business Type"
              variant="outlined"
              fullWidth
              select
              rules={{ required: 'Business type is required' }}
              slotProps={{
                select: {
                  native: true,
                },
              }}
            >
              <option value="">Select a business type</option>
              {mccOptions.map(({ value, label }) => (
                <option key={value || 'other'} value={value}>
                  {label}
                </option>
              ))}
            </TextFieldController>
          </Grid>

          <Grid container spacing={1} size={{ xs: 12, sm: 8 }}>
            <Grid container spacing={1}>
              <Grid size={12}>
                <PlaceSearchBar onSuccess={handleAddressSelect} initialValue={initialAddressValue} />
              </Grid>

              <Grid size={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      Enter address manually
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <ManualAddressInput control={control} />
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          </Grid>

          <Grid container size={{ xs: 12, sm: 8 }} spacing={2}>
            <Grid>
              <Button variant="contained" type="submit" disabled={isLoading || !formState.isDirty}>
                {orgDataComplete ? 'Update' : 'Save'}
              </Button>
            </Grid>
            {orgDataComplete && (
              <Grid>
                <Button variant="outlined" onClick={handleCancel} disabled={isLoading}>
                  Cancel
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </form>
      {!financialInfoComplete && (
        <Box>
          <Card sx={(theme) => ({ backgroundColor: theme.palette.grey[100], my: 4 })}>
            <CardHeader title="Accounting information" />
            <CardContent>
              <FinancialInformationForm org={org} />
            </CardContent>
          </Card>
        </Box>
      )}
    </>
  )
}
